import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Pencil, Check, X, Plus, FolderTree } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DomainesDialog = ({ open, onOpenChange, domaines, formations, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({ nom: '', abreviation: '' });
  const [newDom, setNewDom] = useState({ nom: '', abreviation: '' });
  const [saving, setSaving] = useState(false);

  const nbFormations = (domaineId) => (formations || []).filter(f => f.domaine_id === domaineId).length;

  const startEdit = (d) => {
    setEditing(d.id);
    setEditValues({ nom: d.nom, abreviation: d.abreviation });
  };

  const saveEdit = async (domaineId) => {
    if (!editValues.nom.trim() || !editValues.abreviation.trim()) {
      toast.error("Renseignez le nom et l'abréviation");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/domaines/${domaineId}`, editValues);
      toast.success('Domaine mis à jour');
      setEditing(null);
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const createDomaine = async () => {
    if (!newDom.nom.trim() || !newDom.abreviation.trim()) {
      toast.error("Renseignez le nom et l'abréviation");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/domaines`, newDom);
      toast.success(`Domaine ${newDom.abreviation} créé`);
      setNewDom({ nom: '', abreviation: '' });
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...(domaines || [])].sort((a, b) => (a.abreviation || '').localeCompare(b.abreviation || ''));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="domaines-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-indigo-600" /> Gestion des domaines de formation
          </DialogTitle>
          <DialogDescription>
            L'abréviation est utilisée pour générer les codes des formations (les codes existants ne sont pas modifiés).
          </DialogDescription>
        </DialogHeader>

        {/* Création */}
        <div className="border rounded-lg p-3 bg-slate-50/60 space-y-2">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nouveau domaine</p>
          <div className="flex gap-2">
            <Input
              value={newDom.abreviation}
              onChange={(e) => setNewDom({ ...newDom, abreviation: e.target.value.toUpperCase() })}
              placeholder="Abrév. (ex: LOG)"
              className="w-36"
              data-testid="input-new-dom-abrev"
            />
            <Input
              value={newDom.nom}
              onChange={(e) => setNewDom({ ...newDom, nom: e.target.value })}
              placeholder="Nom du domaine"
              className="flex-1"
              data-testid="input-new-dom-nom"
            />
            <Button size="sm" onClick={createDomaine} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-create-domaine">
              <Plus className="w-4 h-4 mr-1" /> Créer
            </Button>
          </div>
        </div>

        {/* Liste */}
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[130px_1fr_100px_90px] bg-slate-100 px-3 py-2 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
            <span>Abréviation</span>
            <span>Nom</span>
            <span className="text-center">Formations</span>
            <span className="text-center">Action</span>
          </div>
          <div className="divide-y max-h-[340px] overflow-y-auto">
            {sorted.map(d => (
              <div key={d.id} className="grid grid-cols-[130px_1fr_100px_90px] items-center px-3 py-2" data-testid={`domaine-row-${d.abreviation}`}>
                {editing === d.id ? (
                  <>
                    <Input value={editValues.abreviation} onChange={(e) => setEditValues({ ...editValues, abreviation: e.target.value.toUpperCase() })} className="h-7 text-xs mr-2" data-testid="input-edit-dom-abrev" />
                    <Input value={editValues.nom} onChange={(e) => setEditValues({ ...editValues, nom: e.target.value })} className="h-7 text-xs mr-2" data-testid="input-edit-dom-nom" />
                    <span className="text-center text-xs text-slate-500">{nbFormations(d.id)}</span>
                    <div className="flex justify-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => saveEdit(d.id)} disabled={saving} data-testid="btn-save-domaine">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => setEditing(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-mono font-medium text-indigo-700">{d.abreviation}</span>
                    <span className="text-sm text-slate-800 truncate pr-2">{d.nom}</span>
                    <span className="text-center">
                      <Badge variant="outline" className={`text-[10px] ${nbFormations(d.id) === 0 ? 'text-slate-400' : 'text-slate-600'}`}>{nbFormations(d.id)}</Badge>
                    </span>
                    <div className="flex justify-center">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600" onClick={() => startEdit(d)} data-testid={`btn-edit-domaine-${d.abreviation}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {sorted.length === 0 && (
              <p className="text-center py-8 text-sm text-slate-400">Aucun domaine pour le moment</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
